import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import type { BaseIconProps, Size } from "@/assets/icons/utils";
import { cn } from "@/components/podkit/lib/cn";
import { TextWrapper } from "@/components/util/TextWrapper";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ButtonHTMLAttributes } from "react";

export const ButtonVariants = cva(
    cn(
        "select-none",
        "inline-flex items-center gap-1 font-medium justify-center whitespace-nowrap transition-colors",
        "rounded-lg border-0",
        "disabled:border-opacity-0 disabled:text-content-tertiary disabled:pointer-events-none disabled:shadow-none",
        "focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-1 focus-visible:animate-focus-pulse motion-reduce:animate-none active:outline-0",
        "focus:ring-0", // only needed because we set it globally in index.css
    ),
    {
        variants: {
            variant: {
                primary: cn(
                    "bg-surface-button-primary text-content-primary-inverted",
                    "hover:bg-surface-button-primary-accent disabled:opacity-50 disabled:bg-surface-primary-inverted disabled:text-content-primary-inverted",
                    "focus-visible:outline-border-brand",
                ),
                secondary: cn(
                    "bg-surface-button-secondary text-content-primary",
                    "hover:bg-surface-button-secondary-accent disabled:opacity-50 disabled:text-content-primary",
                    "focus-visible:outline-border-brand",
                ),
                brand: cn(
                    "bg-surface-button-primary text-content-primary-inverted",
                    "hover:bg-surface-button-primary-accent disabled:opacity-50 disabled:bg-surface-primary-inverted disabled:text-content-primary-inverted",
                    "focus-visible:outline-border-brand",
                ),
                destructive: cn(
                    "bg-surface-button-destructive text-content-destructive-subtle",
                    "hover:bg-surface-button-destructive-accent disabled:opacity-50 disabled:text-content-destructive-subtle",
                    "focus-visible:outline-border-error-strong",
                ),
                "destructive-secondary": cn(
                    "bg-surface-button-clear border border-border-error-strong text-content-destructive",
                    "hover:bg-surface-destructive-subtle hover:text-content-destructive data-[state=open]:bg-surface-destructive-subtle data-[state=open]:text-content-destructive",
                    "disabled:opacity-50 disabled:border-opacity-50",
                    "focus-visible:outline-border-error-strong",
                ),
                ghost: cn(
                    "bg-surface-button-clear text-content-primary",
                    "hover:bg-surface-button-clear-accent hover:text-content-accent data-[state=open]:bg-surface-button-clear-accent data-[state=open]:text-content-accent",
                    "disabled:opacity-50 disabled:text-content-primary",
                    "focus-visible:outline-border-brand",
                ),
                link: cn(
                    "bg-surface-button-clear text-content-primary",
                    "hover:underline disabled:opacity-50 disabled:text-content-primary",
                    "focus-visible:outline-border-brand",
                ),
                // Note(mads): Text variant is experimental, will talk to Cory to see if we can consolidate with the link variant
                text: cn(
                    "h-4 border-0 bg-transparent p-0 text-content-secondary shadow-none hover:cursor-pointer hover:bg-transparent hover:underline focus-visible:underline focus-visible:outline-0 focus-visible:animate-none",
                    "disabled:bg-transparent",
                    "inline-flex min-w-0 shrink",
                ),
                outline: cn(
                    "bg-surface-button-clear hover:bg-surface-button-clear-accent data-[state=open]:bg-surface-button-clear-accent border border-border-base text-content-primary hover:text-content-accent data-[state=open]:text-content-accent",
                    "disabled:border-opacity-1",
                    "focus-visible:outline-border-brand",
                ),
            },
            size: {
                lg: "gap-2 px-8 py-2 h-10 text-base",
                md: "gap-2 px-4 py-2 h-9 text-base",
                sm: "gap-2 px-3 py-2 h-8 text-base",
                xs: "gap-2 px-2 py-1.5 h-6 text-sm",
            },
        },
        defaultVariants: {
            variant: "primary",
            size: "md",
        },
    },
);

export type ButtonVariant = VariantProps<typeof ButtonVariants>["variant"];

export type ButtonSize = VariantProps<typeof ButtonVariants>["size"];

export type ButtonProps = {
    asChild?: boolean;
    loading?: boolean;
    LeadingIcon?: React.ComponentType<BaseIconProps>;
    TrailingIcon?: React.ComponentType<BaseIconProps>;
    _disableTranslateWrapping?: boolean;
    /**
     * When true, clicking this button will not produce `dashboard_clicked` events.
     * Sets the data-track-disable="true" attribute on the rendered element.
     */
    disableTracking?: boolean;
} & ButtonHTMLAttributes<HTMLButtonElement> &
    VariantProps<typeof ButtonVariants>;

/**
 * Notes for converting from Figma component properties
 * - iconOnly is achieved by not passing in children and providing a LeadingIcon
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            className,
            variant,
            size,
            asChild = false,
            LeadingIcon,
            TrailingIcon,
            loading,
            disabled,
            children,
            disableTracking,
            ...props
        },
        ref,
    ) => {
        const Comp = asChild ? Slot : "button";
        let iconSize: Size = "base";
        switch (size) {
            case "xs":
            case "sm":
                iconSize = "sm";
                break;
            case "md":
                iconSize = "base";
                break;
            case "lg":
                iconSize = "lg";
                break;
        }

        let leftElement;
        let rightElement;
        if (loading) {
            leftElement = <IconSpinner className="animate-spin" size={iconSize} aria-hidden={true} />;
        } else if (LeadingIcon) {
            leftElement = <LeadingIcon size={iconSize} aria-hidden={true} />; // we typically don't want to announce the icon to screen readers
        }

        if (TrailingIcon) {
            rightElement = <TrailingIcon size={iconSize} aria-hidden={true} />;
        }

        // Icon-only buttons should be square with no padding
        let iconOnlyOverrides = "";
        if (!children && LeadingIcon && !TrailingIcon) {
            iconOnlyOverrides = "aspect-square p-0";
        }

        return (
            <TextWrapper
                asChild={asChild}
                leftElement={leftElement}
                rightElement={rightElement}
                className={cn(ButtonVariants({ variant, size }), iconOnlyOverrides, className)}
                disableTracking={disableTracking}
                _disableTranslateWrapping={props._disableTranslateWrapping}
                ref={ref}
                disabled={disabled || loading}
                aria-busy={loading}
                as={Comp as React.ElementType}
                {...props}
            >
                {children}
            </TextWrapper>
        );
    },
);
Button.displayName = "Button";
